import { CommonModule, DatePipe, NgIf, UpperCasePipe } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AssetDto, DisplayUserDto, GalleryAccessLevel, GalleryDto, TenantDto } from '@picflow/types';
import { fixContentEditableStyles } from 'src/app/lib/fix-contenteditable-styles';
import { getPermissions } from 'src/app/lib/get-allowed-feedback-options';
import { sleep } from 'src/app/lib/sleep';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AssetService } from 'src/app/services/asset.service';
import { CleanUserGeneratedHtmlService } from 'src/app/services/clean-user-generated-html.service';
import { CommentService } from 'src/app/services/comment.service';
import { ExifService, NormalizedExif } from 'src/app/services/exif.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { StateService } from 'src/app/services/state.service';
import { ClickTrackDirective } from 'src/app/shared/directives/click-track.directive';
import { VideoResolutionTypePipe } from 'src/app/shared/pipes/video-resolution-type.pipe';
import { PermissionsPipe } from '../../../shared-extra/pipes/permissions.pipe';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { DataUnitsPipe } from '../../../shared/pipes/data-units.pipe';
import { FloorPipe } from '../../../shared/pipes/floor.pipe';
import { MegapixelsPipe } from '../../../shared/pipes/megapixels.pipe';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { AssetTagsComponent } from '../asset-tags/asset-tags.component';
import { AssetVersionMenuComponent } from '../asset-version-menu/asset-version-menu.component';

@Component({
  selector: 'app-asset-info',
  templateUrl: './asset-info.component.html',
  styleUrls: ['./asset-info.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    WysiwygDirective,
    ButtonDirective,
    SvgComponent,
    AssetTagsComponent,
    ContextMenuComponent,
    AssetVersionMenuComponent,
    UpperCasePipe,
    DatePipe,
    DataUnitsPipe,
    MegapixelsPipe,
    PermissionsPipe,
    ClickTrackDirective,
    FloorPipe,
    VideoResolutionTypePipe,
  ],
})
export class AssetInfoComponent implements OnChanges {
  @ViewChild('description', { static: false }) description: ElementRef<HTMLDivElement>;
  @ViewChild('descriptionInput', { static: false }) descriptionInput: ElementRef<HTMLDivElement>;
  @Input() asset: AssetDto;
  @Input() gallery: GalleryDto;
  @Input() accessLevel: GalleryAccessLevel;
  tenant: TenantDto;
  exif: NormalizedExif;
  creator: DisplayUserDto;
  allowVersionHistory: boolean;
  edittingDescription = false;
  expandedDescription = false;
  newAssetDescription: string;
  descriptionTooBig: boolean;
  assetDescription: string;
  loadingCreator = true;

  constructor(
    private exifService: ExifService,
    private commentService: CommentService,
    private state: StateService,
    private assetService: AssetService,
    private _sanitizer: DomSanitizer,
    private cleanUserGeneratedHtmlService: CleanUserGeneratedHtmlService,
    private analytics: AnalyticsService,
    private galleryService: GalleryService,
    public featureFlags: FeatureFlagsService
  ) {}

  private resetDescription() {
    this.assetDescription = this.cleanUserGeneratedHtmlService.clean(
      this.asset.description || this.exif?.description || ''
    );
    this.newAssetDescription = this.assetDescription || '';
    if (this.assetDescription === '\n') this.assetDescription = '';
    this.assetDescription = this._sanitizer.sanitize(SecurityContext.NONE, this.assetDescription);
  }

  async ngOnChanges() {
    this.exif = this.exifService.getExif(this.asset);
    this.tenant = this.gallery['tenant']?.id ? this.gallery['tenant'] : this.state.activeTenant;

    this.resetDescription();

    this.allowVersionHistory = this.gallery.version_history_enabled || this.accessLevel === 'edit';
    const permissions = getPermissions(this.gallery);
    if (!permissions.asset.assetVersions) {
      this.allowVersionHistory = false;
    }

    this.creator = await this.getCreator();
    this.loadingCreator = false;
    await sleep(0);
    this.calcDescriptionTooBig();
  }

  async saveDescription() {
    this.asset.description = fixContentEditableStyles(this.newAssetDescription);
    this.edittingDescription = false;

    if (this.asset.description === '') this.asset.description = '\n';
    if (this.asset.description === '/auto') this.asset.description = '';

    this.analytics.track('save_description');

    this.ngOnChanges();
    this.calcDescriptionTooBig();

    await this.assetService.updateAsset(
      this.asset.id,
      { description: this.asset.description },
      <string>this.asset.gallery,
      <string>this.asset.section
    );
  }

  async cancelEditDescription() {
    this.resetDescription();
    this.edittingDescription = false;
    await sleep(0);
    this.calcDescriptionTooBig();
  }

  async expandDescription() {
    this.expandedDescription = true;
    await sleep(0);
    this.calcDescriptionTooBig();
  }

  async shrinkDescription() {
    this.expandedDescription = false;
    await sleep(0);
    this.calcDescriptionTooBig();
    this.description.nativeElement.scrollTop = 0;
  }

  async startEditing(isNew = false) {
    this.edittingDescription = true;
    await sleep(0);
    this.descriptionInput?.nativeElement.focus();
    if (isNew) {
      this.analytics.track('add_description');
    } else {
      this.analytics.track('edit_description');
    }
  }

  convertFrameRate(frameRate: string) {
    const [numerator, denominator] = frameRate.split('/');
    return Math.round(parseInt(numerator) / parseInt(denominator));
  }

  private calcDescriptionTooBig() {
    if (!this.description) return;
    this.descriptionTooBig = this.description.nativeElement.clientHeight < this.description.nativeElement.scrollHeight;
  }

  private async getCreator(): Promise<DisplayUserDto> {
    const firstComment = await this.commentService.getFirstComment(this.asset.id, this.gallery.id);
    if (firstComment) {
      if (firstComment.asset_version_event && firstComment['asset_version'] === this.asset.version) {
        return firstComment.user;
      }
      const comments = await this.commentService.getComments(this.asset.id, this.gallery.id);
      const comment = comments.find(c => c.asset_version_event);
      if (comment) return comment.user;
    }

    const gallery = await this.galleryService.getGallery(this.gallery.id, { expandCreatedBy: true });
    return gallery['created_by'];
  }
}
